import { HTTPError } from "ky";
import { useEffect, useState } from "react";
import InviteModal from "./components/InviteModal";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { useAuth } from "./hooks/useAuth";
import { useCanvases } from "./hooks/useCanvases";
import {
  createCanvas,
  deleteCanvas,
  inviteUser,
  respondToInvitation,
  saveSubscription,
} from "./lib/api";
import { subscribeToPushNotifications } from "./lib/push";
import { AuthPage } from "./pages/AuthPage";
import { Canvas as CanvasComponent } from "./pages/Canvas";
import { HomePage } from "./pages/HomePage";

function AppContent() {
  const { token, user, setToken, logout } = useAuth();
  const { canvases, refresh, error: canvasError } = useCanvases(token, user?.userId || null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [inviteState, setInviteState] = useState<{
    open: boolean;
    id: number | null;
    user: string;
    error: string | null;
  }>({ open: false, id: null, user: "", error: null });
  const [actionError, setActionError] = useState<string | null>(null);
  const { addToast } = useToast();

  // Reset selection on logout
  useEffect(() => {
    if (!token) setSelectedId(null);
  }, [token]);

  // Global badge reset
  useEffect(() => {
    const reset = () => {
      navigator.clearAppBadge?.();
      navigator.serviceWorker?.controller?.postMessage({
        type: "RESET_BADGE_COUNT",
      });
    };
    reset(); // Run on mount
    window.addEventListener("focus", reset);
    return () => window.removeEventListener("focus", reset);
  }, []);

  const handleLogout = async () => {
    await logout();
    setSelectedId(null);
  };

  const handleApi = async (fn: () => Promise<unknown>, onSuccess?: () => void) => {
    setActionError(null);
    try {
      await fn();
      onSuccess?.();
    } catch (e) {
      let message = (e as Error).message;
      if (e instanceof HTTPError) {
        try {
          const body = await e.response.json<{ error: string }>();
          message = body.error;
        } catch {
          /* ignore */
        }
      }
      setActionError(message);
    }
  };

  const sendInvite = async () => {
    if (!inviteState.id || !inviteState.user.trim()) return;
    setInviteState((s) => ({ ...s, error: null }));
    try {
      await inviteUser(inviteState.id, inviteState.user.trim());
      setInviteState({ open: false, id: null, user: "", error: null });
      addToast("invited!", "success");
    } catch (e) {
      let message = (e as Error).message;
      if (e instanceof HTTPError) {
        try {
          const body = await e.response.json<{ error: string }>();
          message = body.error;
        } catch {
          // Fallback to default message
        }
      }
      setInviteState((s) => ({
        ...s,
        error: message,
      }));
    }
  };

  const error = canvasError || actionError;

  if (selectedId && token) {
    return (
      <CanvasComponent
        canvasId={selectedId}
        onBack={() => setSelectedId(null)}
        token={token}
        onTurnEnd={refresh}
      />
    );
  }
  if (!token) {
    return (
      <div className="main-container">
        <AuthPage onAuth={setToken} />
      </div>
    );
  }

  return (
    <div className="main-container">
      {error && <div className="alert-box">{error}</div>}
      <HomePage
        username={user?.username || ""}
        userId={user?.userId || 0}
        onLogout={handleLogout}
        onCanvasSelect={setSelectedId}
        canvases={canvases}
        onCreateCanvas={() => handleApi(createCanvas, refresh)}
        onDeleteCanvas={(id) => handleApi(() => deleteCanvas(id), refresh)}
        onInvitationResponse={(id, status) =>
          handleApi(() => respondToInvitation(id, status), refresh)
        }
        onInvite={(id) => setInviteState({ open: true, id, user: "", error: null })}
        onSubscribeToPush={async () => {
          try {
            const result = await subscribeToPushNotifications();
            if (result) {
              if (result.isNew) {
                await saveSubscription(result.subscription);
                addToast("you're now registered for notifications!", "success");
              } else {
                addToast("you are already registered for notifications.", "info");
              }
            } else {
              addToast("couldn't register for notifications.", "error");
            }
          } catch (e) {
            addToast((e as Error).message, "error");
          }
          return "Notification" in window ? Notification.permission : "default";
        }}
      />
      <InviteModal
        isOpen={inviteState.open}
        username={inviteState.user}
        onUsernameChange={(u) => setInviteState((s) => ({ ...s, user: u, error: null }))}
        onInvite={sendInvite}
        onClose={() => setInviteState((s) => ({ ...s, open: false }))}
        notification={inviteState.error || undefined}
      />
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}
